import {
  AllowedHandleTypeComponent,
  CompartmentComponent,
  CoreApi,
  FrontLayoutComponent,
  OpeningFunction,
  Product,
  ProductInfoComponent,
  ProductType,
  SlidingDoorComponent
} from '@inter-ikea-storageone/range';
import { BestaOpeningFunctionComponent } from '@inter-ikea-storageone/range-besta';

import { reportSelectReadyLayout } from 'analytics/reporters';
import {
  ComponentIds,
  Connection,
  Entity,
  HierarchyUtils,
  ParentComponent,
  HostConnectionsComponent,
  PartPair,
  PointSeriesSubConnector,
  ProductComponent,
  ProductUtils,
  AssemblyStructureComponent
} from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { getMajorityOfArray } from 'rangeConfig/shared/utils/colorUtils/utils';
import * as Utils from './frontLayouts'; // Import this module into itself to be able to mock single functions during test
import { getRange } from 'utils/queryUtils';

export const getMajorityDesign = (
  frontEntities: Entity[]
): { name: string; design: string } | null => {
  const frontDesigns = frontEntities
    .map(front => {
      const productInfo = front.getComponent<ProductInfoComponent>(ProductInfoComponent.id);
      if (!productInfo) return undefined;
      return `${productInfo.productName},${productInfo.design}`;
    })
    .filter((d): d is string => d !== undefined);

  if (frontDesigns.length === 0) return null;

  const majority = getMajorityOfArray(frontDesigns);
  return { name: majority.split(',')[0], design: majority.split(',')[1] };
};

export const getMajorityProduct = (entities: Entity[]) => {
  const coreProductIds = entities
    .map(
      entity => entity.getComponent<ProductComponent>(ComponentIds.PRODUCT_COMPONENT_ID)?.productRef
    )
    .filter((h): h is string => h !== null);
  return coreProductIds.length > 0 ? getMajorityOfArray(coreProductIds) : null;
};

export const getChildrenEntitiesInCompartment = (frame: Entity, compartment: Entity) => {
  const connectorsInCompartment =
    compartment.getComponent<CompartmentComponent>(CompartmentComponent.id)
      ?.connectorsInCompartment || [];
  const frameGuestConnections =
    frame.getComponent<HostConnectionsComponent>(ComponentIds.HOST_CONNECTIONS_COMPONENT_ID)
      ?.connections || [];

  const entitiesInCompartment = frameGuestConnections.reduce((acc: Entity[], curr: Connection) => {
    const hostConnector = curr.hostConnector as PointSeriesSubConnector;
    const connectorSeriesKey = hostConnector.series?.key;
    if (connectorsInCompartment.includes(connectorSeriesKey)) {
      acc.push(curr.guestConnectorEntity);
    }
    return acc;
  }, []);
  return entitiesInCompartment;
};

export const getChildrenForFrame = (frame: Entity, compartment?: Entity) => {
  const frameAssembly =
    HierarchyUtils.findWithComponentInUpwardsHierarchy(frame, ComponentIds.FRAME_COMPONENT_ID) ||
    frame;

  const framesInStack = HierarchyUtils.descendants(frameAssembly).filter(child =>
    child.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
  );
  return compartment
    ? getChildrenEntitiesInCompartment(frameAssembly, compartment)
    : HierarchyUtils.filterInHierarchy(frameAssembly, child => {
        const hasStackedParent = framesInStack.some(e => HierarchyUtils.hasAncestor(child, e));
        if (hasStackedParent) {
          return false;
        }
        return true;
      });
};

export const getFrontsOnFrame = (frame: Entity, compartment?: Entity) => {
  return Utils.getChildrenForFrame(frame, compartment).filter(entity => {
    const productType = entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
      ?.productType;
    return (
      (entity.hasComponent(ComponentIds.DOOR_COMPONENT_ID) ||
        entity.hasComponent(ComponentIds.DRAWER_COMPONENT_ID) ||
        entity.hasComponent(SlidingDoorComponent.id)) &&
      productType !== ProductType.SMASTAD_PULL_OUT_FRAME_DRAWER
    );
  });
};

export const frameHasFronts = (frame: Entity): boolean => {
  return getFrontsOnFrame(frame)?.length > 0;
};

export const getShelvesInFrame = (frame: Entity, compartment?: Entity) => {
  const shelfProductTypes = [
    ProductType.BESTA_SHELF,
    ProductType.BESTA_GLASS_SHELF,
    ProductType.BESTA_BOTTLE_RACK,
    ProductType.PLATSA_SHELF,
    ProductType.SMASTAD_SHELF
  ];
  const children = Utils.getChildrenForFrame(frame, compartment);
  return children.filter(entity => {
    const productType = entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
      ?.productType;
    return productType && shelfProductTypes.includes(productType);
  });
};

export const getFrontsInScene = (coreApi: CoreApi) => {
  return coreApi.entities
    .getByComponents([
      ComponentIds.DOOR_COMPONENT_ID,
      ComponentIds.DRAWER_COMPONENT_ID,
      SlidingDoorComponent.id
    ])
    .filter(
      front => !coreApi.entities.isProductType(front, ProductType.SMASTAD_PULL_OUT_FRAME_DRAWER)
    );
};

export const getAllowedHandleTypes = (front: Entity) => {
  return (
    front.getComponent<AllowedHandleTypeComponent>(AllowedHandleTypeComponent.id)?.handleTypes || [
      ProductType.HANDLE,
      ProductType.KNOB,
      ProductType.EDGE_HANDLE
    ]
  );
};

export const hasOpeningFunction = (product: Product, openingFunction: string) => {
  if (product.meta?.allowedHandleTypes && product.meta?.allowedHandleTypes.length === 0) {
    return !!product.parts.find(part => part.product?.type === ProductType.BESTA_SOFT_CLOSING);
  }
  return !!product.parts.find(part => part.product?.type === openingFunction);
};

export const createFrontLayoutEntityWithDesign = (
  frontLayoutProduct: Product,
  frame: Entity,
  products: Product[],
  coreApi: CoreApi,
  frontDesign: { name: string; design: string }
) => {
  const frameDesign = frame.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design;
  const productDTO = coreApi
    .getFrontLayoutProductCatalog()
    ?.findProductDTO(frontLayoutProduct.coreProductId);

  if (!productDTO || !productDTO.template.parts) {
    return null;
  }

  const replaceParts = productDTO.template.parts
    .map(part => {
      const partProduct = products.find(p => p.coreProductId === part.id);
      if (!partProduct) return undefined;

      let product;
      if (
        partProduct.type === ProductType.PLATSA_SHELF ||
        partProduct.type === ProductType.BESTA_SHELF
      ) {
        product = products.find(
          p =>
            p.communicatedDimensions.depth === partProduct.communicatedDimensions.depth &&
            p.communicatedDimensions.width === partProduct.communicatedDimensions.width &&
            p.type === partProduct.type &&
            p.design === frameDesign
        );
      } else if (
        partProduct.type === ProductType.BESTA_DRAWER_38_ASM ||
        partProduct.type === ProductType.BESTA_DRAWER_26_ASM
      ) {
        product = products.find(
          p =>
            p.name === frontDesign.name &&
            p.design === frontDesign.design &&
            p.type === partProduct.type &&
            hasOpeningFunction(p, getMajorityOpeningFunctionInScene(coreApi))
        );
      } else {
        product = products.find(
          p =>
            p.name === frontDesign.name &&
            p.design === frontDesign.design &&
            p.type === partProduct.type &&
            p.communicatedDimensions.width === partProduct.communicatedDimensions.width &&
            p.communicatedDimensions.height === partProduct.communicatedDimensions.height &&
            p.communicatedDimensions.depth === partProduct.communicatedDimensions.depth
        );
      }
      return product ? new PartPair(part.partKey, product.coreProductId) : undefined;
    })
    .filter((p): p is PartPair => p !== undefined);
  return coreApi
    .getFrontLayoutProductCatalog()
    ?.createCustomAssembly(
      frontLayoutProduct.coreProductId,
      replaceParts,
      coreApi.getServices().productManager
    );
};

export const createDefaultFrontLayoutEntity = (
  frontLayoutProduct: Product,
  frame: Entity,
  products: Product[],
  coreApi: CoreApi
) => {
  if (frontLayoutProduct.type === ProductType.BESTA_FRONT_LAYOUT) {
    const frameDesign = frame.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design;
    const productDTO = coreApi
      .getFrontLayoutProductCatalog()
      ?.findProductDTO(frontLayoutProduct.coreProductId);
    if (productDTO && productDTO.template.parts) {
      const replaceParts = productDTO.template.parts
        .map(part => {
          const partProduct = products.find(p => p.coreProductId === part.id);
          let product;
          if (
            partProduct &&
            getMajorityOpeningFunctionInScene(coreApi) === ProductType.BESTA_SOFT_CLOSING &&
            (partProduct.type === ProductType.BESTA_DRAWER_38_ASM ||
              partProduct.type === ProductType.BESTA_DRAWER_26_ASM)
          ) {
            // Default BESTA layouts are built with push-to-open drawer so need to replace these if current opening function is soft closing
            product = products.find(
              p =>
                p.name === partProduct.name &&
                p.design === partProduct.design &&
                p.type === partProduct.type &&
                hasOpeningFunction(p, ProductType.BESTA_SOFT_CLOSING)
            );
            return product ? new PartPair(part.partKey, product.coreProductId) : undefined;
          } else if (partProduct && partProduct.type === ProductType.BESTA_SHELF) {
            // Default BESTA layouts are built with white shelves so need to replace them to match frame design
            product = products.find(
              p =>
                p.communicatedDimensions.depth === partProduct.communicatedDimensions.depth &&
                p.communicatedDimensions.width === partProduct.communicatedDimensions.width &&
                p.type === partProduct.type &&
                p.design === frameDesign
            );
          }
          return product ? new PartPair(part.partKey, product.coreProductId) : undefined;
        })
        .filter((p): p is PartPair => p !== undefined);
      return coreApi
        .getFrontLayoutProductCatalog()
        ?.createCustomAssembly(
          frontLayoutProduct.coreProductId,
          replaceParts,
          coreApi.getServices().productManager
        );
    }
  }
  return coreApi.getServices().productManager.createEntity(frontLayoutProduct.coreProductId);
};

export const deleteFrontLayout = (
  frameEntity: Entity,
  coreApi: CoreApi,
  compartment?: Entity,
  deleteShelves: boolean = false
) => {
  const frontsOnFrame = getFrontsOnFrame(frameEntity, compartment);
  const shelvesInFrame = deleteShelves ? getShelvesInFrame(frameEntity, compartment) : [];
  [...frontsOnFrame, ...shelvesInFrame].forEach(entity => {
    coreApi.entities.delete(entity);
  });
};

export const addFrontStyleToFrame = (
  frontStyleProduct: Product,
  frame: Entity,
  products: Product[],
  coreApi: CoreApi
) => {
  const frontsOnFrame = getFrontsOnFrame(frame);

  if (frontsOnFrame.length > 0) {
    // Replace existing fronts
    frontsOnFrame.forEach(front => {
      const frontType = front.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
        ?.productType;
      const frontSize = front.getComponent<CommunicationSizeComponent>(
        ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
      )?.size;
      let newFront;
      if (
        frontType === ProductType.BESTA_DRAWER_26_ASM ||
        frontType === ProductType.BESTA_DRAWER_38_ASM
      ) {
        newFront = products.find(
          p =>
            p.type === frontType &&
            p.name === frontStyleProduct.name &&
            p.design === frontStyleProduct.design &&
            hasOpeningFunction(p, getMajorityOpeningFunctionInScene(coreApi))
        );
      } else {
        newFront = products.find(
          p =>
            p.type === frontType &&
            p.communicatedDimensions.width === frontSize?.width &&
            p.communicatedDimensions.height === frontSize?.height &&
            p.communicatedDimensions.depth === frontSize?.depth &&
            p.name === frontStyleProduct.name &&
            p.design === frontStyleProduct.design
        );
      }
      if (newFront) {
        coreApi.products.replace(front.id, newFront.coreProductId);
      }
    });
  } else {
    // Add default front layout to frame
    const frameDefaultFrontLayout = frame.getComponent<FrontLayoutComponent>(
      FrontLayoutComponent.id
    )?.defaultLayoutId;
    const frontLayoutProduct = products.find(p => p.coreProductId === frameDefaultFrontLayout);

    if (!frontLayoutProduct) return;

    const frontLayoutEntity = Utils.createFrontLayoutEntityWithDesign(
      frontLayoutProduct,
      frame,
      products,
      coreApi,
      {
        name: frontStyleProduct.name,
        design: frontStyleProduct.design
      }
    );

    if (!frontLayoutEntity) return;
    const deleteShelves = shouldDeleteShelves(frontLayoutEntity, frame, coreApi);

    Utils.addFrontLayoutEntityToFrame(frontLayoutEntity, frame, coreApi, deleteShelves);
  }
};

export const replaceFrontStyle = (
  frontToChangeTo: Product,
  frontToReplace: Entity,
  products: Product[],
  coreApi: CoreApi
) => {
  const frontType = frontToReplace.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
    ?.productType;
  // Replace existing fronts
  const frontSize = frontToReplace.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size;
  let newFront;
  if (
    frontType === ProductType.BESTA_DRAWER_26_ASM ||
    frontType === ProductType.BESTA_DRAWER_38_ASM
  ) {
    newFront = products.find(
      p =>
        p.type === frontType &&
        p.name === frontToChangeTo.name &&
        p.design === frontToChangeTo.design &&
        hasOpeningFunction(p, getMajorityOpeningFunctionInScene(coreApi))
    );
  } else {
    newFront = products.find(
      p =>
        p.type === frontType &&
        p.communicatedDimensions.width === frontSize?.width &&
        p.communicatedDimensions.height === frontSize?.height &&
        p.communicatedDimensions.depth === frontSize?.depth &&
        p.name === frontToChangeTo.name &&
        p.design === frontToChangeTo.design
    );
  }
  if (newFront) {
    coreApi.products.replace(frontToReplace.id, newFront.coreProductId);
  }
};

const shouldDeleteShelves = (frontLayout: Entity, frame: Entity, coreApi: CoreApi) => {
  switch (getRange()) {
    case 'smastad':
      const frameType = coreApi.entities.getProductType(frame);
      return (
        frameType === ProductType.SMASTAD_DESK || frameType === ProductType.SMASTAD_CHANGING_TABLE
      );
    case 'besta':
      const assemblyParts = frontLayout.getComponent<AssemblyStructureComponent>(
        ComponentIds.ASSEMBLY_STRUCTURE_COMPONENT_ID
      )?.parts;
      if (assemblyParts) {
        const includesShelves = !!assemblyParts.find(p =>
          p.partKey.startsWith(ProductType.BESTA_SHELF)
        );
        const onlyDrawers = assemblyParts.every(
          p =>
            p.partKey.startsWith(ProductType.BESTA_DRAWER_26_ASM) ||
            p.partKey.startsWith(ProductType.BESTA_DRAWER_38_ASM)
        );
        return includesShelves || onlyDrawers;
      }
      return false;
    case 'platsa':
      return false;
    default:
      return false;
  }
};

export const addFrontLayoutToFrame = (
  frontLayout: Product,
  frame: Entity,
  products: Product[],
  coreApi: CoreApi,
  design: {
    name: string;
    design: string;
  } | null,
  compartment?: Entity
) => {
  const frontLayoutEntity = design
    ? Utils.createFrontLayoutEntityWithDesign(frontLayout, frame, products, coreApi, design)
    : Utils.createDefaultFrontLayoutEntity(frontLayout, frame, products, coreApi);

  if (!frontLayoutEntity) return;

  const deleteShelves = shouldDeleteShelves(frontLayoutEntity, frame, coreApi);

  reportSelectReadyLayout({ layout: frontLayout.coreProductId });
  return Utils.addFrontLayoutEntityToFrame(
    frontLayoutEntity,
    frame,
    coreApi,
    deleteShelves,
    compartment
  );
};

export const addFrontLayoutEntityToFrame = (
  frontLayout: Entity,
  frame: Entity,
  coreApi: CoreApi,
  deleteShelves: boolean = true,
  compartment?: Entity
) => {
  const handlesOnFrame = coreApi.entities.getDescendantsWithComponent(
    frame,
    ComponentIds.HANDLE_COMPONENT_ID
  );
  const handlesInScene = coreApi.entities.getByComponent(ComponentIds.HANDLE_COMPONENT_ID);
  const handleToAdd =
    handlesOnFrame.length > 0
      ? getMajorityProduct(handlesOnFrame)
      : getMajorityProduct(handlesInScene);

  Utils.deleteFrontLayout(frame, coreApi, compartment, deleteShelves);
  let placementResult;
  if (compartment) {
    const connector = compartment.getComponent<CompartmentComponent>(CompartmentComponent.id)
      ?.connectorKey;
    if (connector) {
      placementResult = coreApi.placementService.snapToConnector(
        frontLayout,
        'front-layout-frame',
        frame,
        connector
      );
    }
  } else {
    placementResult = coreApi.placementService.snapToConnector(
      frontLayout,
      'front-layout-frame',
      frame,
      'center'
    );
  }

  if (placementResult && !placementResult.valid) {
    if (frame.hasComponent(ComponentIds.SELECTED_COMPONENT_ID)) {
      frame.removeComponent(ComponentIds.OUTLINED_COMPONENT_ID);

      // All selected entites descendants also get an outline component added when selecting the parent
      // We need to remove this in order to display conflicts outlines correctly.
      // When the conflict is removed we re-add the outlined component to the selected entity again
      const descendants = frame
        .getComponent<ParentComponent>(ComponentIds.PARENT_COMPONENT_ID)
        ?.allDescendants();

      descendants?.forEach(childEntity => {
        childEntity.removeComponent(ComponentIds.OUTLINED_COMPONENT_ID);
      });
    }
    coreApi.createConflict(placementResult, undefined, frontLayout);
  }

  // Removed snap strategy
  coreApi.placementService.dissolveEntity(frontLayout);
  if (handleToAdd) {
    const frontEntitesOnFrame = Utils.getFrontsOnFrame(frame, compartment);
    frontEntitesOnFrame.forEach(front => {
      if (
        front.hasComponent(ComponentIds.DOOR_COMPONENT_ID) ||
        (front.hasComponent(ComponentIds.DRAWER_COMPONENT_ID) &&
          front.getComponent<BestaOpeningFunctionComponent>(BestaOpeningFunctionComponent.id)
            ?.openingFunction !== ProductType.BESTA_PUSH_OPEN)
      ) {
        coreApi.products.addToTarget(handleToAdd, front);
      }
    });
  }

  return placementResult;
};

export const getFrontOpeningFunctionsInScene = (coreApi: CoreApi) => {
  const frontsInScene = coreApi.entities.getByComponents([
    ComponentIds.DRAWER_COMPONENT_ID,
    ComponentIds.DOOR_COMPONENT_ID
  ]);
  return frontsInScene
    .map(
      front =>
        front.getComponent<BestaOpeningFunctionComponent>(BestaOpeningFunctionComponent.id)
          ?.openingFunction
    )
    .filter((openingFunction): openingFunction is OpeningFunction => !!openingFunction);
};

export const getMajorityOpeningFunctionInScene = (coreApi: CoreApi) => {
  const openingFunctions = getFrontOpeningFunctionsInScene(coreApi);
  return openingFunctions.length > 0
    ? getMajorityOfArray(openingFunctions)
    : ProductType.BESTA_PUSH_OPEN;
};

export const changeAllDrawersOpeningFunction = (
  openingFunction: OpeningFunction,
  products: Product[],
  coreApi: CoreApi,
  addMajorityHandle: boolean = true
) => {
  const drawersInScene = coreApi.entities
    .getByComponent(ComponentIds.DRAWER_COMPONENT_ID)
    .filter(drawer => getAllowedHandleTypes(drawer).length > 0);
  coreApi.history.historyAction(() => {
    drawersInScene.forEach(drawer => {
      changeDrawerOpeningFunction(drawer, openingFunction, products, coreApi, addMajorityHandle);
    });
  });
};

export const changeAllOpeningFunctionInScene = (
  openingFunction: OpeningFunction,
  products: Product[],
  coreApi: CoreApi,
  addMajorityHandle: boolean = true
) => {
  const doorsInScene = coreApi.entities.getByComponent(ComponentIds.DOOR_COMPONENT_ID);
  const drawersInScene = coreApi.entities
    .getByComponent(ComponentIds.DRAWER_COMPONENT_ID)
    .filter(drawer => getAllowedHandleTypes(drawer).length > 0);
  coreApi.history.historyAction(() => {
    doorsInScene.forEach(door => {
      door.addComponent(new BestaOpeningFunctionComponent(openingFunction));
    });
    drawersInScene.forEach(drawer => {
      changeDrawerOpeningFunction(drawer, openingFunction, products, coreApi, addMajorityHandle);
    });
  });
};

export const changeDrawerOpeningFunction = (
  drawer: Entity,
  openingFunction: OpeningFunction,
  products: Product[],
  coreApi: CoreApi,
  addMajorityHandle: boolean = true
) => {
  coreApi.history.historyAction(() => {
    const drawerOpeningFunction = drawer.getComponent<BestaOpeningFunctionComponent>(
      BestaOpeningFunctionComponent.id
    )?.openingFunction;
    if (drawerOpeningFunction === openingFunction) return;
    const drawerSelected = drawer.hasComponent(ComponentIds.SELECTED_COMPONENT_ID);
    const drawerProductInfo = drawer.getComponent<ProductInfoComponent>(ProductInfoComponent.id);
    const replacementDrawer = products.find(
      p =>
        p.globalItemNo === drawerProductInfo?.globalArticleNbr &&
        p.type === drawerProductInfo?.productType &&
        hasOpeningFunction(p, openingFunction)
    );
    if (!replacementDrawer) return;
    const handle = HierarchyUtils.descendants(drawer).find((child: Entity) =>
      child.hasComponent(ComponentIds.HANDLE_COMPONENT_ID)
    );
    if (openingFunction === ProductType.BESTA_PUSH_OPEN && handle) {
      coreApi.entities.delete(handle);
    }
    const placementResult = coreApi.products.replace(drawer.id, replacementDrawer.coreProductId);
    if (placementResult?.replacedEntityResult.placedEntity) {
      if (openingFunction === ProductType.BESTA_SOFT_CLOSING && addMajorityHandle) {
        const handlesInScene = coreApi.entities.getByComponent(ComponentIds.HANDLE_COMPONENT_ID);
        const majorityHandle = getMajorityProduct(handlesInScene);
        if (majorityHandle) {
          coreApi.products.addToTarget(
            majorityHandle,
            placementResult.replacedEntityResult.placedEntity
          );
        }
      }

      if (drawerSelected) {
        coreApi.entities.select(placementResult?.replacedEntityResult.placedEntity);
      }
    }
  });
};

export const getFrameSize = (coreApi: CoreApi, frame: Entity) => {
  const frameSize = coreApi.entities.getEntityComSize(frame);
  return {
    height: frameSize ? frameSize.height : 0,
    width: frameSize ? frameSize.width : 0
  };
};

export const getPartiallyOpenFrameSize = (coreApi: CoreApi, frame: Entity) => {
  const frameSize = getFrameSize(coreApi, frame);
  const children = ProductUtils.immediateConnectionRootDescendants(frame);

  const doorChildren = children.filter(child => child.hasComponent(ComponentIds.DOOR_COMPONENT_ID));

  const uniqueDoorChildren = doorChildren.filter(
    (child, i) =>
      doorChildren.findIndex(
        c =>
          coreApi.entities.getWithPosition(c)?.position.y ===
          coreApi.entities.getWithPosition(child)?.position.y
      ) === i
  );

  const totalDoorsHeight = uniqueDoorChildren.reduce((acc, curr) => {
    const size = coreApi.entities.getEntityComSize(curr);
    return size ? acc + size.height : acc + 0;
  }, 0);

  const drawerChildren = children.filter(child =>
    child.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)
  );

  const totalDrawerHeight = drawerChildren.reduce((acc, curr) => {
    const size = coreApi.entities.getEntityComSize(curr);
    return size ? acc + size.height : acc + 0;
  }, 0);

  const openSpaceHeight = frameSize.height - (totalDoorsHeight + totalDrawerHeight);

  const openSpaceSize = {
    width: frameSize.width,
    height: openSpaceHeight
  };

  return openSpaceSize;
};
